<template>
  <div class="c-employee-details c-employee-details-u" :class="{'edit-mode-active': editMode}">
    <v-dialog v-model="show">
      <v-card v-if="employee && (editMode || employee.Employee_ID)"
              :class="{'edit-mode': editMode}">
        <v-card-title class="align-start">
          <!-- Photo -->
          <v-col class="pa-0 px-sm-2" cols="auto">
            <v-img
              :lazy-src="require('@/assets/profile-placeholder-250x250.png')"
              height="250"
              width="250"
              cover
              :src="imgSrc"
            >
            <div class="d-flex flex-column">
              <a :href="imgSrc" :download="`${employee.Full_Name}.jpg`" style="text-decoration: none">
                <v-icon
                  v-if="canEdit && (this.employee.Photo && this.employee.Photo.data)"
                  class="ml-2"
                  color="success"
                >
                  mdi-download-circle
                </v-icon>
              </a>
              <v-tooltip v-if="editMode" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <label class="photo-uploader ml-2" v-bind="attrs" v-on="on">
                    <v-icon color="primary">mdi-pencil-circle</v-icon>
                    <v-file-input
                      v-model="fileInputValue"
                      class="d-none"
                      accept="image/png, image/jpeg, image/bmp"
                      @change="onPhotoUploaderChange"
                      hide-input
                    />
                  </label>
                </template>
                Photo requirements:<br>
                - Formats: JPG, PNG, BMP<br>
                - Max. size: 2MB<br>
                - Resolution up to 1024x1024px
              </v-tooltip>
            </div>
              <h3 v-if="photoIsLoading" class="loading-photo" > Loading... </h3>
            </v-img>
          </v-col>
          <!-- /Photo -->
          <!-- Contacts -->
          <v-col class="pa-0 px-sm-2 d-flex flex-column" cols="12" md="3">
            <v-form
              v-model="contactsValid"
              lazy-validation
            >
              <div>
                <h3>{{ editMode ? fullName : employee.Full_Name }}</h3>
                <h5>{{ employee.Status === 'Status error' ? null : employee.Status }}</h5>
                <div>
                  <h5>Birthday:
                    <span v-if="editMode && !employeeBirth" class="error--text">*</span>
                    <span
                      v-if="!editMode"
                      class="subtitle-1"
                      >
                        {{ employeeBirth }}
                    </span>
                    <div :style="editMode ? 'margin-top:-12px;' : ''">
                      <DatePicker
                        v-if="editMode"
                        v-model="employeeBirth"
                        :rules="birthDateRules"
                      />
                    </div>
                  </h5>
                </div>
              </div>
              <div v-if="employee.Cellular || employee.e_mail || editMode">
                <h5>Contacts:</h5>
                <div
                  class="employee-cellular-container"
                  :class="{
                    'show-copy-btn': employee.Cellular &&  !editMode
                  }"
                >
                  <v-btn
                    v-if="employee.Cellular && !editMode"
                    link
                    :href="`tel:${employee.Cellular}`"
                    text
                    small
                    color="primary"
                    class="px-2"
                  >
                    <v-icon small class="mr-2">mdi-phone</v-icon>
                    {{ employee.Cellular }}
                  </v-btn>
                  <v-text-field
                    v-else-if="editMode"
                    :rules="phoneRules"
                    dense
                    v-model="employee.Cellular"
                    prepend-icon="mdi-phone">
                  </v-text-field>
                  <!-- Copy button -->
                  <v-tooltip bottom color="primary">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        color="primary"
                        class="copy-btn"
                        x-small
                        rounded
                        fab
                        v-bind="attrs"
                        v-on="on"
                        @click="copyToClipboard(employee.Cellular, 'Phone')"
                      >
                        <v-icon small>
                          mdi-content-copy
                        </v-icon>
                      </v-btn>
                    </template>
                    <span>Copy</span>
                  </v-tooltip>
                  <!-- /Copy button -->
                </div>
                <v-divider v-if="employee.e_mail && !editMode"></v-divider>
                <div
                  class="employee-email-container"
                  :class="{
                    'show-copy-btn': employee.e_mail && !editMode
                  }"
                >
                  <v-btn
                    v-if="employee.e_mail && !editMode"
                    link
                    target="_blank"
                    rel="noopener noreferrer"
                    :href="`https://mail.google.com/mail/u/0/?view=cm&fs=1&tf=1&to=${employee.e_mail}`"
                    text
                    small
                    color="primary"
                    class="px-2"
                  >
                    <v-icon small class="mr-2">mdi-gmail</v-icon>
                    {{ employee.e_mail }}
                  </v-btn>
                  <v-text-field
                    v-else-if="editMode"
                    dense
                    :rules="emailRules"
                    v-model="employee.e_mail"
                    prepend-icon="mdi-gmail">
                  </v-text-field>
                  <!-- Copy button -->
                  <v-tooltip bottom color="primary">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                      color="primary"
                      class="copy-btn"
                      x-small
                      fab
                      v-bind="attrs"
                      v-on="on"
                      @click="copyToClipboard(employee.e_mail, 'Email')"
                      >
                        <v-icon small>
                          mdi-content-copy
                        </v-icon>
                      </v-btn>
                    </template>
                    <span>Copy</span>
                  </v-tooltip>
                  <!-- /Copy button -->
                </div>
                <v-divider v-if="employee.e_mail && !editMode"></v-divider>
                <div class="employee-google-chat-container">
                  <v-tooltip bottom color="primary">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-if="employee.e_mail && !editMode"
                        text
                        small
                        color="primary"
                        class="px-2"
                        v-bind="attrs"
                        v-on="on"
                        @click="copyToClipboard(employee.e_mail, 'Google Chat')"
                      >
                        <v-img
                          class="mr-2"
                          style="margin-top: 2px;"
                          :src="require('@/assets/google_chat_icon.svg')"
                          width="16"
                          height="16"
                          contain
                        />
                        Google Chat
                      </v-btn>
                      <v-icon small class="ml-2" v-if="!editMode"  @click="showMessangersDialog = true" color="primary">mdi-information</v-icon>
                    </template>
                    <span>Click to copy email to start a chat</span>
                  </v-tooltip>
                </div>
                <div v-if="editMode" class="d-flex align-center" style="margin-top: -5px; margin-bottom: 10px;">
                <span class="text-subtitle-2 mx-1">Messengers:</span>
                <v-tooltip bottom color="primary">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="success"
                      v-bind="attrs"
                      v-on="on"
                      @click.prevent="openMessangerDialog(false)"
                    >
                      <v-icon>mdi-plus-circle</v-icon>
                    </v-btn>
                  </template>
                  <span>Add new messenger</span>
                </v-tooltip>
                <v-tooltip bottom color="primary">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="primary"
                      v-bind="attrs"
                      v-on="on"
                      @click.prevent="showMessangersDialog = true"
                    >
                      <v-icon>mdi-information</v-icon>
                    </v-btn>
                  </template>
                  <span>Show messengers list (edit/delete)</span>
                </v-tooltip>
              </div>
              </div>
            </v-form>
          </v-col>
          <!-- /Contacts -->
          <v-col class="pa-0 px-sm-2 d-flex flex-column" cols="12" md="2" lg="2">
            <div class="mb-1" v-if="employee.Working_Hours || editMode">
              <h5>Working hours:</h5>
              <div
                v-if="!editMode"
                class="subtitle-1 pt-0"
              >
                {{workingHours}} <v-icon small v-if="!editMode && employee.Working_Hours.length > 25"  @click="showWorkingHoursDialog = true" color="primary">mdi-information</v-icon>
              </div>
              <v-text-field
                v-if="editMode"
                v-model="employee.Working_Hours"
                dense
                hide-details
                class="mt-0 pt-0"
              ></v-text-field>
            </div>

            <!-- Work load section -->
            <div class="mb-1">
              <div class="d-flex align-baseline">
                <h5 class="mb-0 mr-1">Work load:</h5>
                <div
                  v-show="employeeWorkload.Workload || editMode"
                  class="subtitle-1 pt-0 workload-container"
                >
                  <div v-show="!editMode">
                    {{ workLoadDisplay }}
                  </div>
                  <v-text-field
                    v-show="editMode"
                    v-model="employeeWorkload.Workload"
                    type="number"
                    min="0"
                    max="100"
                    suffix="%"
                    class="workload-input"
                    hide-details
                    density="compact"
                    style="max-width: 80px"
                  />
                </div>
              </div>
            </div>
            <div class="mt-0">
              <h5>Working Experience:</h5>
              <div class="subtitle-1" v-if="!editMode">
                {{ employee.Previous_Experience ? `Previous: ${computedPreviousExperience}` : '' }}
                <br v-if="employee.Previous_Experience">
                {{ employee.WorkDuration ? `Devcom: ${devComExperience}` : ''}}
              </div>
              <div v-if="editMode">
                <v-text-field
                  class="inputAmount mt-1 pt-2"
                  type="number"
                  v-model="employee.Previous_Experience"
                  label="Previous Experience"
                ></v-text-field>
              </div>
            </div>
            <div class="mt-0" v-if="(HR_Financier_Manager || currentEmployee) && employee.Employee_ID">
              <h5>Emergency Contact:</h5>
              <v-text-field
                v-if="editMode"
                class="pt-0 mt-0"
                readonly
                v-model="Emergency_Contact.Contact_FullName"
                dense
                hide-details
              >
                <template v-slot:append v-if="editMode">
                  <v-btn
                    v-if="editMode"
                    small
                    icon
                    color="primary"
                    @click.prevent="emergencyContactDialog = true"
                  >
                    <v-icon>mdi-pencil-circle</v-icon>
                  </v-btn>
                </template>
              </v-text-field>
              <div
                v-else
                class="text-subtitle-1"
              >
                {{ Emergency_Contact.Emergency_Contact_ID && Emergency_Contact.Contact_Info ? emergencyContact : 'No Emergency Contact' }}
                <v-icon
                  small
                  v-if="!editMode && (Emergency_Contact.Contact_FullName + ', ' + Emergency_Contact.Contact_Info).length > 25"
                  @click="emergencyContactDialog = true" color="primary">mdi-information
                </v-icon>
              </div>
            </div>
          </v-col>
          <v-col cols="12" md="5" lg="5" class="pa-0 px-sm-2 d-flex flex-column max-width-400">
            <div v-if="(HR_Financier_Manager || currentEmployee) && employee.Employee_ID">
              <v-data-table
                :mobile-breakpoint="320"
                :headers="absenceHeaders"
                :items="absenceItems"
                calculate-widths
                class="max-width-300 border-thin"
                hide-default-footer
                dense
              >
                <template v-slot:[`header.AbsenceType`]="{ header }">
                  <span class="subtitle-3">{{ header.text }}</span>
                </template>
                <template v-slot:[`header.Used`]="{ header }">
                  <span class="subtitle-3">{{ header.text }}</span>
                </template>
                <template v-slot:[`header.Left`]="{ header }">
                  <span class="subtitle-3">{{ header.text }}</span>
                </template>
                <template v-slot:[`item.AbsenceType`]="{ item }">
                  <span class="subtitle-3"> {{ item.AbsenceType }} </span>
                </template>
                <template v-slot:[`item.Used`]="{ item }">
                  <span class="subtitle-3"> {{ item.Used }} </span>
                </template>
                <template v-slot:[`item.Left`]="{ item }">
                  <span class="subtitle-3"> {{ item.Left }} </span>
                </template>
              </v-data-table>
            </div>
            <v-row class="pl-3 pt-3" v-if="(HR_Financier_Manager || currentEmployee) && employee.Employee_ID">
              <div v-if="!editMode" class="d-flex align-center flex-nowrap">
                <v-btn
                  text
                  small
                  :disabled="!EmployeeCV.CV_ID && !EmployeeCV.CV_Link"
                  :href="EmployeeCV.CV_Link"
                  :title="EmployeeCV.CV_Link"
                  target="_blank"
                  rel="noopener noreferrer"
                  color="primary"
                  class="text-subtitle-2 pl-0 pr-1 text-no-wrap"
                >
                  DevCom CV
                </v-btn>

                <v-btn
                  text
                  small
                  :disabled="!Career_Matrix.Career_Matrix_ID && !Career_Matrix.Career_Matrix_Link"
                  :href="Career_Matrix.Career_Matrix_Link"
                  :title="Career_Matrix.Career_Matrix_Link"
                  target="_blank"
                  rel="noopener noreferrer"
                  color="primary"
                  class="text-subtitle-2 px-1 text-no-wrap"
                >
                  Career Matrix
                </v-btn>

                <v-btn
                  text
                  small
                  :disabled="!employeePDP.PDP_Link_ID && !employeePDP.PDP_Link"
                  :href="employeePDP.PDP_Link"
                  :title="employeePDP.PDP_Link"
                  target="_blank"
                  color="primary"
                  class="text-subtitle-2 px-1 text-no-wrap"
                >
                  PDP
                </v-btn>

                <v-menu v-model="selfReviewMenu" bottom offset-y :close-on-content-click="true">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      text
                      small
                      color="primary"
                      class="text-subtitle-2 px-1 text-no-wrap"
                      v-bind="attrs"
                      v-on="on"
                    >
                      Performance Self-Review
                      <v-icon right small class="ml-1">mdi-chevron-down</v-icon>
                    </v-btn>
                  </template>
                  <v-card class="self-review-menu-card">
                    <v-list dense class="pa-0">
                      <v-list-item
                        :disabled="!Self_Review_Survey.Performance_Self_Review_ID && !Self_Review_Survey.Survey_Form_Link"
                        :href="Self_Review_Survey.Survey_Form_Link"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <v-list-item-content>
                          <v-list-item-title>Survey Form</v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-icon small color="grey lighten-1">mdi-open-in-new</v-icon>
                        </v-list-item-action>
                      </v-list-item>
                      <v-divider></v-divider>
                      <v-list-item
                        :disabled="!Self_Review_Responses.Performance_Self_Review_ID && !Self_Review_Responses.Responses_Link"
                        :href="Self_Review_Responses.Responses_Link"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <v-list-item-content>
                          <v-list-item-title>Responses</v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-icon small color="grey lighten-1">mdi-open-in-new</v-icon>
                        </v-list-item-action>
                      </v-list-item>
                    </v-list>
                  </v-card>
                </v-menu>
              </div>

              <div v-else class="w-100">
                <v-text-field
                  label="Link To DevCom CV"
                  v-model="EmployeeCV.CV_Link"
                >
                  <template v-slot:append>
                    <v-btn small icon color="success" :disabled="!EmployeeCV.CV_Link" @click="updateEmployeeCV">
                      <v-icon>mdi-check-circle</v-icon>
                    </v-btn>
                    <v-btn small icon color="red darken-2" :disabled="!EmployeeCV.CV_ID" @click="deleteEmployeeCV">
                      <v-icon>mdi-delete-circle</v-icon>
                    </v-btn>
                  </template>
                </v-text-field>

                <v-text-field
                  label="Link To Career Matrix"
                  class="pt-0"
                  v-model="Career_Matrix.Career_Matrix_Link"
                >
                  <template v-slot:append>
                    <v-btn small icon color="success" :disabled="!Career_Matrix.Career_Matrix_Link" @click="updateCareerMatrix">
                      <v-icon>mdi-check-circle</v-icon>
                    </v-btn>
                    <v-btn small icon color="red darken-2" :disabled="!Career_Matrix.Career_Matrix_ID" @click="deleteCareerMatrix">
                      <v-icon>mdi-delete-circle</v-icon>
                    </v-btn>
                  </template>
                </v-text-field>

                <v-text-field
                  label="Link To PDP"
                  class="pt-0"
                  v-model="employeePDP.PDP_Link"
                >
                  <template v-slot:append>
                    <v-btn small icon color="success" :disabled="!employeePDP.PDP_Link" @click="updateEmployeePDPLink">
                      <v-icon>mdi-check-circle</v-icon>
                    </v-btn>
                    <v-btn small icon color="red darken-2" :disabled="!employeePDP.PDP_Link_ID" @click="deleteEmployeePDPLink">
                      <v-icon>mdi-delete-circle</v-icon>
                    </v-btn>
                  </template>
                </v-text-field>

                <v-text-field
                  label="Link To Self-Review Survey Form"
                  class="pt-0"
                  v-model="Self_Review_Survey.Survey_Form_Link"
                >
                  <template v-slot:append>
                    <v-btn small icon color="success" :disabled="!Self_Review_Survey.Survey_Form_Link" @click="updateSelfReviewSurvey">
                      <v-icon>mdi-check-circle</v-icon>
                    </v-btn>
                    <v-btn
                      small
                      icon
                      color="red darken-2"
                      :disabled="!Self_Review_Survey.Performance_Self_Review_ID || !Self_Review_Survey.Survey_Form_Link"
                      @click="deleteSelfReviewSurvey"
                    >
                      <v-icon>mdi-delete-circle</v-icon>
                    </v-btn>
                  </template>
                </v-text-field>

                <v-text-field
                  label="Link To Self-Review Responses"
                  class="pt-0"
                  v-model="Self_Review_Responses.Responses_Link"
                >
                  <template v-slot:append>
                    <v-btn small icon color="success" :disabled="!Self_Review_Responses.Responses_Link" @click="updateSelfReviewResponses">
                      <v-icon>mdi-check-circle</v-icon>
                    </v-btn>
                    <v-btn
                      small
                      icon
                      color="red darken-2"
                      :disabled="!Self_Review_Responses.Performance_Self_Review_ID || !Self_Review_Responses.Responses_Link"
                      @click="deleteSelfReviewResponses"
                    >
                      <v-icon>mdi-delete-circle</v-icon>
                    </v-btn>
                  </template>
                </v-text-field>
              </div>
            </v-row>
            <v-row class="pl-3 pt-0" v-if="HRorFinancier && employee.Employee_ID">
              <v-text-field
                ref="estimateInvoice"
                :label="currentEmployeeEstimateInvoice.from ? `Employee Estimate Invoice From(${currentEmployeeEstimateInvoice.from})` : 'Employee Estimate Invoice'"
                :readonly="!editMode"
                v-model="newInvoiceEstimateValue"
                :value="currentEmployeeEstimateInvoice.value"
              >
                <template v-slot:append>
                  <v-btn
                    v-if="newInvoiceEstimateValue && newInvoiceEstimateValue !== currentEmployeeEstimateInvoice.value"
                    small
                    color="primary"
                    @click="onEmployeeEstimateInvoiceSave()"
                  >
                    Save
                  </v-btn>
                  <v-btn
                    v-if="newInvoiceEstimateValue === currentEmployeeEstimateInvoice.value"
                    small
                    text
                    plain
                    color="primary"
                    @click.prevent="showInvoiceHistory = true"
                  >
                    Show History
                  </v-btn>
                </template>
              </v-text-field>
            </v-row>
          </v-col>
          <!-- /Contacts -->
          <v-btn class="close-dialog-btn" icon @click.prevent="show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-col cols="12">
            <v-tabs show-arrows v-model="employeeTabs">
              <v-tab>
                Profile
              </v-tab>
              <v-tab
                v-if="canViewPdp && employee.Employee_ID"
              >
                Performance Review
              </v-tab>
              <v-tab
                v-if="(HRorFinancier || currentEmployee) && employee.Employee_ID"
              >
                Personal Policy
              </v-tab>
              <v-tab
                v-if="(HR_Financier_Manager || currentEmployee || employeeSupervisor) && employee.Employee_ID"
              >
                Certificates
              </v-tab>
              <v-tab
                v-if="employee.Employee_ID && hasPermissionPayroll"
              >
                Payroll
              </v-tab>
              <v-tab
                v-if="currentEmployee"
              >
                Requests
              </v-tab>
              <v-tab
                v-if="HR_Financier_Manager && employee.Employee_ID"
              >
                Projects
              </v-tab>
              <v-tab
                v-if="(HR_Financier_AssetsManager || currentEmployee) && employee.Employee_ID"
              >
                Assets
              </v-tab>
              <v-tab
                v-if="(HRorFinancier || currentEmployee) && employee.Employee_ID"
              >
                Documents
              </v-tab>
              <v-tab
                v-if="(canAccessMilitaryInfo || currentEmployee) && employee.Employee_ID"
              >
                Military Info
              </v-tab>
              <v-tab
                v-if="(HRorFinancier || currentEmployee) && employee.Employee_ID"
              >
                Personal Data
              </v-tab>
            </v-tabs>
          </v-col>
        </v-card-title>
        <v-card-text class="py-4 pt-1">
          <v-tabs-items :touchless="true" v-model="employeeTabs" class="py-3">
            <v-tab-item>
              <EmployeeProfile
                v-model="employee"
                :editMode="editMode"
                :hasPermissionRate="employee.Employee_ID ? hasPermissionPayroll : false"
              />
            </v-tab-item>
            <v-tab-item
              v-if="canViewPdp && employee.Employee_ID"
            >
              <PdpTabs
                :employee="employee"
                :rolesOfUser="rolesOfCurrentUser"
                :canViewSummary="canViewPrmSummary"
              />
            </v-tab-item>
            <v-tab-item
              v-if="(HRorFinancier || currentEmployee) && employee.Employee_ID"
            >
              <EmployeePolicies
                :employee="employee"
                :isTab="true"
              />
            </v-tab-item>
            <v-tab-item
              v-if="(HR_Financier_Manager || currentEmployee || employeeSupervisor) && employee.Employee_ID"
            >
              <EmployeeCertificates
                :employee="employee"
                :rolesOfUser="rolesOfCurrentUser"
              />
            </v-tab-item>
            <v-tab-item
              v-if="employee.Employee_ID && hasPermissionPayroll"
            >
              <EmployeePayroll
                v-if="employee.Employee_ID && hasPermissionPayroll"
                :employee="employee"
              />
            </v-tab-item>
            <v-tab-item
              v-if="currentEmployee"
            >
              <EmployeeRequests
                :employee="employee"
              />
            </v-tab-item>
            <v-tab-item
              v-if="HR_Financier_Manager && employee.Employee_ID"
            >
              <EmployeeProjectsHistory
                :employeeID="employee.Employee_ID"
                :rolesOfUser="rolesOfCurrentUser"
              />
            </v-tab-item>
            <v-tab-item
              v-if="(HR_Financier_AssetsManager || currentEmployee) && employee.Employee_ID"
            >
              <EmployeeAssets
                :employeeID="employee.Employee_ID"
                :rolesOfUser="rolesOfCurrentUser"
              />
            </v-tab-item>
            <v-tab-item
              v-if="(HRorFinancier || currentEmployee) && employee.Employee_ID"
            >
              <EmployeeDocuments
                :employee="employee"
                :user="user"
              />
            </v-tab-item>
            <v-tab-item
              v-if="(canAccessMilitaryInfo || currentEmployee) && employee.Employee_ID"
            >
              <EmployeeMilitaryInfo
                :employee="employee"
                :user="user"
                :rolesOfUser="rolesOfCurrentUser"
                :canEditMilitaryInfo="canAccessMilitaryInfo"
              />
            </v-tab-item>
            <v-tab-item
              v-if="(HRorFinancier || currentEmployee) && employee.Employee_ID"
            >
              <EmployeePersonalData
                v-model="employee"
                :user="user"
                :editMode="editMode"
              />
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
        <v-divider v-if="canEdit"></v-divider>
        <v-card-actions v-if="canEdit && employeeTabs === 0" class="flex-wrap">
          <v-col cols="auto" class="px-1 py-1">
            <v-btn
              color="primary"
              v-if="employee.Employee_ID"
              outlined
              small
              @click.prevent="changePassword.showDialog = true"
            >Change password</v-btn>
          </v-col>
          <v-col cols="auto" class="px-1 py-1">
            <v-btn
              color="primary"
              v-if="employee.Employee_ID && employee.Employee_ID !== user.employee_id"
              outlined
              small
              @click.prevent="goToReportsNewPage"
            >Employee's Reports</v-btn>
          </v-col>
          <v-col cols="auto" class="px-1 py-1" v-if="employee.Employee_ID">
            <v-btn
              color="primary"
              outlined
              small
              @click.prevent="goToEmployeeSkills"
            >Employee's Skills</v-btn>
          </v-col>
          <v-spacer class="d-none d-sm-block"></v-spacer>
          <v-col cols="12" sm="auto" class="px-2 py-1">
            <v-btn color="primary"
              v-if="editMode"
              small
              outlined
              @click.prevent="toggleOnCancel"
              class="mr-2"
            >Cancel</v-btn>
            <v-btn color="red darken-2 white--text"
              v-if="canDelete"
              small
              @click.prevent="deleteEmployeeDialog = true"
            >
              Delete
            </v-btn>
            <v-btn color="primary"
              class="ml-2"
              v-if="employee.Is_Deleted"
              small
              @click.prevent="restoreEmployee"
            >
              Restore
            </v-btn>
            <v-btn
              color="primary"
              class="ml-2"
              small
              :disabled="editMode && (canSave || !contactsValid || !emplInfoWasChanged)"
              @click.prevent="toggleEdit"
            >
              {{ editMode ? 'Save': 'Edit' }}
            </v-btn>
          </v-col>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <div v-if="show">
      <!-- Remove dialog -->
      <ConfirmOrDeniedDialog
        v-if="showRemove"
        v-model="showRemove"
        :mode="confirmOrDeniedDialogModes.delete"
        @onDeleteClick="submitRemove"
        @onCancelClick="showRemove = false"
      />
      <!-- Alert image dialog -->
      <ConfirmOrDeniedDialog
      v-if="showDeniedDialog"
      v-model="showDeniedDialog"
      :title="'Image size is too big'"
      :message="'Please crop it. Max resolution is 1024px/1024px'"
      :mode="confirmOrDeniedDialogModes.denied"
      @onOkClick="showDeniedDialog = !showDeniedDialog"
      @onCloseClick="showDeniedDialog = !showDeniedDialog"
    />
      <!-- /Remove dialog -->
      <!-- Delete employee dialog -->
      <v-dialog
        v-model="deleteEmployeeDialog"
        max-width="500"
      >
        <v-card>
          <v-card-title>
            Are you sure?
          </v-card-title>
          <v-card-text>
            <v-textarea
              spellcheck="false"
              v-if="employee"
              v-model="employee.Deleted_Notes"
              label="Deleted notes"
            ></v-textarea>
          </v-card-text>
          <v-card-actions>
            <v-btn color="primary" small outlined @click.prevent="deleteEmployeeDialog = false">Cancel</v-btn>
            <v-spacer></v-spacer>
            <v-btn color="red darken-2 white--text" small @click.prevent="deleteEmployee">Delete</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <!-- /Delete employee dialog -->
      <!-- Change password dialog -->
      <v-dialog
        v-model="changePassword.showDialog"
        max-width="500"
      >
        <v-card>
          <v-card-title>
            Change password
            <v-spacer></v-spacer>
            <v-btn
              icon
              @click.prevent="changePassword.showDialog = false"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-text-field
              v-model="changePassword.old"
              label="Old password"
              :type="changePassword.oldType"
            >
              <template v-slot:append>
                <v-btn icon
                  @mousedown="changePassword.oldType = 'text'"
                  @mouseup="changePassword.oldType = 'password'"
                >
                  <v-icon>{{changePassword.oldType === 'password' ? 'mdi-eye-outline' : 'mdi-eye-off-outline'}}</v-icon>
                </v-btn>
              </template>
            </v-text-field>
            <v-text-field
              v-model="changePassword.new"
              label="New password"
              :type="changePassword.newType"
            >
              <template v-slot:append>
                <v-btn icon
                  @mousedown="changePassword.newType = 'text'"
                  @mouseup="changePassword.newType = 'password'"
                >
                  <v-icon>{{changePassword.newType === 'password' ? 'mdi-eye-outline' : 'mdi-eye-off-outline'}}</v-icon>
                </v-btn>
              </template>
            </v-text-field>
            <v-text-field
              v-model="changePassword.confirmNew"
              label="Confirm new password"
              :type="changePassword.confirmNewType"
            >
              <template v-slot:append>
                <v-btn icon
                  @mousedown="changePassword.confirmNewType = 'text'"
                  @mouseup="changePassword.confirmNewType = 'password'"
                >
                  <v-icon>{{changePassword.confirmNewType === 'password' ? 'mdi-eye-outline' : 'mdi-eye-off-outline'}}</v-icon>
                </v-btn>
              </template>
            </v-text-field>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn small color="primary" @click="changeEmployeePassword" :disabled="disableChangePasswordSave || changePassword.new !== changePassword.confirmNew">Save</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <!-- /Change password dialog -->
      <!-- Messanger dialog -->
      <v-dialog
        max-width="550px"
        v-model="showMessangersDialog"
        scrollable
      >
        <v-card>
          <v-card-title>
            Messengers list
            <v-spacer></v-spacer>
            <v-btn
              icon
              @click.prevent="showMessangersDialog = false"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pt-3">
            <v-data-table
              dense
              :items="employee.Employee_IM"
              :headers="IMHeaders"
              :items-per-page="-1"
              hide-default-footer
            >
              <template v-slot:[`item.Actions`]="{ item }">
                <v-btn icon small
                  color="primary"
                  @click.prevent="openMessangerDialog(item)"
                >
                  <v-icon>mdi-pencil-circle</v-icon>
                </v-btn>
                <v-btn icon small
                  color="red darken-2"
                 @click.prevent="openRemoveDialog('messangerId', item.IM_ID)"
                >
                  <v-icon>mdi-delete-circle</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-dialog>
      <!-- /Messanger dialog -->
      <!-- Messanger add dialog -->
      <v-dialog
        v-model="messanger.showDialog"
        max-width="290"
      >
        <v-card>
          <v-card-title>
            {{ messanger.title }}
            <v-spacer></v-spacer>
            <v-btn
              icon
              @click.prevent="messanger.showDialog = false"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-text-field
              label="Messenger"
              v-model="messanger.data.title"
            ></v-text-field>
            <v-text-field
              label="Account"
              v-model="messanger.data.account"
            ></v-text-field>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              small
              color="primary"
              @click.prevent="saveMessangers"
              :disabled="disableMessangerSave"
            >Save</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <!-- /Messanger add dialog -->
      <!-- Emergency contact dialog -->
      <v-dialog
        v-model="emergencyContactDialog"
        max-width="290"
      >
        <v-card>
          <v-card-title>
            Emergency Contact
            <v-spacer></v-spacer>
            <v-btn
              icon
              @click.prevent="emergencyContactDialog = false"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-text-field
              label="Contact"
              :readonly="!editMode"
              v-model="Emergency_Contact.Contact_FullName"
            ></v-text-field>
            <v-textarea
              label="Info"
              :readonly="!editMode"
              v-model="Emergency_Contact.Contact_Info"
              rows="2"
            ></v-textarea>
          </v-card-text>
          <v-card-actions v-if="editMode">
            <v-spacer></v-spacer>
            <v-btn
              small
              color="primary"
              @click.prevent="updateEmergencyContact"
            >Save</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <!-- /Emergency contact dialog -->
      <!-- Cancel changes dialog -->
      <v-dialog
        v-model="showCancelChangesDialog"
        max-width="15%"
      >
        <v-card>
          <v-card-title>
            Cancel changes?
          </v-card-title>
          <v-card-actions>
            <v-btn small outlined width="6.5em" color="primary" @click="showCancelChangesDialog = false">
              No
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn small color="primary" @click="onCancel()">
              Yes
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <!-- /Cancel changes dialog -->
      <!-- Working duration dialog -->
      <v-dialog
        max-width="400px"
        v-model="showWorkingHoursDialog"
        scrollable
      >
        <v-card>
          <v-card-title>
            Working Hours
            <v-spacer></v-spacer>
            <v-btn
              icon
              @click.prevent="showWorkingHoursDialog = false"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <div class="pt-2">
            <h3>{{ employee.Working_Hours }}</h3>
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>
      <!-- /Working duration dialog -->
      <EmployeeSkillsProfile
        v-if="showCategorySkillsEditor"
        v-model="showCategorySkillsEditor"
        :employee="editedEmployeeForSkills"
      />
      <!-- Estimated Invoice Hours History -->
      <!-- Working duration dialog -->
      <v-dialog
        max-width="400px"
        v-model="showInvoiceHistory"
        scrollable
      >
        <v-card>
          <v-card-title>
            Estimated Invoice Hours History
            <v-spacer></v-spacer>
            <v-btn
              icon
              @click.prevent="showInvoiceHistory = false"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pt-2">
            <v-data-table
              dense
              :headers="invoiceHistoryHeaders"
              :items="historyOfEmployeeEstimateInvoice"
              :items-per-page="-1"
              hide-default-footer
            >
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-dialog>
      <!-- /Estimated Invoice Hours History -->
    </div>
  </div>
</template>

<script>
import { canViewPrm, canViewSummary } from '@/utils/prmAccess'
import { getApiErrorMessage } from '@/utils/apiError'
import { formatLocalDate } from '@/utils/dateUtils'

export default {
  name: 'EmployeeDetails',
  components: {
    ConfirmOrDeniedDialog: () => import('@/components/global/ConfirmOrDeniedDialog.vue'),
    PdpTabs: () => import('@/components/employee/employee_details_tabs/PdpTabs.vue'),
    EmployeePolicies: () => import('@/components/employee/employee_details_tabs/EmployeePolicies.vue'),
    EmployeeCertificates: () => import('@/components/employee/employee_details_tabs/EmployeeCertificates.vue'),
    EmployeeProfile: () => import('@/components/employee/employee_details_tabs/EmployeeProfile.vue'),
    EmployeePayroll: () => import('@/components/employee/employee_details_tabs/EmployeePayroll.vue'),
    EmployeeRequests: () => import('@/components/employee/employee_details_tabs/EmployeeRequests.vue'),
    EmployeeProjectsHistory: () => import('@/components/employee/employee_details_tabs/EmployeeProjectsHistory.vue'),
    EmployeeAssets: () => import('@/components/employee/employee_details_tabs/EmployeeAssets.vue'),
    EmployeeDocuments: () => import('@/components/employee/employee_details_tabs/EmployeeDocuments.vue'),
    DatePicker: () => import('@/components/global/DatePicker.vue'),
    EmployeeSkillsProfile: () => import('@/components/employee_skills/EmployeeSkillsProfile.vue'),
    EmployeeMilitaryInfo: () => import('@/components/employee/employee_details_tabs/EmployeeMilitaryInfo.vue'),
    EmployeePersonalData: () => import('@/components/employee/employee_details_tabs/EmployeePersonalData.vue')
  },
  props: {
    value: {
      required: false,
      type: Boolean,
      default: false
    },
    employee: {
      required: false,
      default: null
    },
    edit: {
      required: false,
      default: false
    },
    notFromEmployeeModule: {
      required: false,
      default: false
    }
  },
  data () {
    return {
      fileInputValue: null,
      showDeniedDialog: false,
      invoiceHistoryHeaders: [
        {
          text: 'Estimated Invoice',
          value: 'Estimated_Invoice',
          divider: true,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: 'From Date',
          divider: true,
          value: 'From_Date',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: '',
          value: 'Actions',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        }
      ],
      newInvoiceEstimateValue: null,
      currentEmployeeEstimateInvoice: {
        value: null,
        from: null
      },
      historyOfEmployeeEstimateInvoice: [],
      employeeWorkload: {
        Employee_Workload_ID: null,
        Employee_ID: null,
        Workload: 100
      },
      disableMessangerSave: false,
      disableChangePasswordSave: false,
      minDateReportingPermission: formatLocalDate(),
      showCancelChangesDialog: false,
      EmployeeCV: {
        CV_ID: null,
        Employee_ID: null,
        CV_Link: null
      },
      Career_Matrix: {
        Career_Matrix_ID: null,
        Employee_ID: null,
        Career_Matrix_Link: null
      },
      selfReviewMenu: false,
      Self_Review_Survey: {
        Performance_Self_Review_ID: null,
        Employee_ID: null,
        Survey_Form_Link: ''
      },
      Self_Review_Responses: {
        Performance_Self_Review_ID: null,
        Employee_ID: null,
        Responses_Link: ''
      },
      emailRules: [
        v => !v || /.+@.+\..+/.test(v) || 'E-mail must be valid',
        v => (v || '').indexOf(' ') < 0 || 'No spaces are allowed'
      ],
      phoneRules: [
        v => !v || /^\+?\d+$/.test(v) || 'Phone must be valid'
      ],
      birthDateRules: [
        v => !!v || 'Birth date is required'
      ],
      contactsValid: true,
      employeeTabs: 0,
      employeeCopy: {},
      birthMenu: false,
      localEdit: null,
      showDeleteEmployeeDialog: false,
      showMessangersDialog: false,
      policies: [],
      messanger: {
        showDialog: false,
        isNew: false,
        title: '',
        data: {
          id: null,
          title: '',
          account: ''
        }
      },
      remove: {
        showDialog: false,
        data: {
          messangerId: null
        }
      },
      changePassword: {
        showDialog: false,
        old: '',
        oldType: 'password',
        new: '',
        newType: 'password',
        confirmNew: '',
        confirmNewType: 'password'
      },
      rolesOfCurrentUser: [],
      absenceHeaders: [
        {
          text: 'Absense Type',
          align: 'center',
          sortable: false,
          value: 'AbsenceType',
          width: '80px',
          divider: true
        },
        {
          text: 'Used(h)',
          sortable: false,
          align: 'center',
          value: 'Used',
          width: '55px',
          divider: true
        },
        {
          text: 'Left(h)',
          sortable: false,
          align: 'center',
          value: 'Left',
          width: '55px'
        }
      ],
      absenceItems: [],
      meetingEditMode: false,
      photoIsLoading: false,
      Emergency_Contact: {
        Emergency_Contact_ID: null,
        Employee_ID: null,
        Contact_FullName: '',
        Contact_Info: ''
      },
      emergencyContactDialog: false,
      employeePDP: {
        PDP_Link_ID: null,
        Employee_ID: null,
        PDP_Link: ''
      },
      showWorkingHoursDialog: false,
      showCategorySkillsEditor: false,
      editedEmployeeForSkills: {
        employeeId: null,
        employeeFullName: ''
      },
      showInvoiceHistory: false
    }
  },
  computed: {
    computedPreviousExperience () {
      if (!this.employee.Previous_Experience) return null

      const totalDays = this.employee.Previous_Experience * 365
      const years = Math.floor(totalDays / 365)
      const months = Math.floor((totalDays % 365) / 30)

      const yearLabel = years === 1 ? 'year' : 'years'
      const monthLabel = months === 1 ? 'month' : 'months'

      if (years && months) {
        return `${years} ${yearLabel} ${months} ${monthLabel}`
      } else if (years) {
        return `${years} ${yearLabel}`
      } else if (months) {
        return `${months} ${monthLabel}`
      }

      return ''
    },
    workLoadDisplay () {
      if (!this.employeeWorkload.Workload) return 'Not specified'
      return `${this.employeeWorkload.Workload}%`
    },
    deleteEmployeeDialog: {
      get () {
        return this.showDeleteEmployeeDialog
      },
      set (val) {
        if (this.employee) {
          if (val) {
            const date = new Date().toLocaleString()
            this.employee.Is_Deleted = true
            this.employee.Deleted_Notes = `Deleted by: ${this.user.login}, Date: ${date}`
            if (!this.employee.Left_Date) {
              this.employee.Left_Date = formatLocalDate(new Date())
            }
          } else {
            this.employee.Is_Deleted = false
            this.employee.Deleted_Notes = null
            if (!this.employee.Left_Date) {
              this.employee.Left_Date = null
            }
          }
        }
        this.showDeleteEmployeeDialog = val
      }
    },
    IMHeaders () {
      const headers =
      [
        {
          text: 'Messenger',
          value: 'IM_NAME',
          divider: true,
          class: 'px-2 text-no-wrap',
          show: true
        },
        {
          text: 'Account',
          value: 'IM_ACCOUNT',
          divider: this.editMode,
          class: 'px-2 text-no-wrap',
          show: true
        },
        {
          text: 'Actions',
          value: 'Actions',
          divider: true,
          class: 'px-2 text-no-wrap',
          width: '20%',
          sortable: false,
          show: this.editMode
        }
      ]
      return headers.filter(h => h.show)
    },
    editMode: {
      get () {
        return this.localEdit || this.edit
      },
      set (val) {
        this.localEdit = val
      }
    },
    showRemove: {
      get () {
        return this.remove.showDialog
      },
      set (val) {
        if (!val) this.closeRemoveDialog()
        this.remove.showDialog = val
      }
    },
    show: {
      get () {
        return this.value
      },
      set (val) {
        if (!val) {
          this.$emit('closeDetails')
        }
        this.$emit('input', val)
      }
    },
    currentEmployeeRoles () {
      return this.$store.getters.getEmployeeRoles
    },
    employeeBirth: {
      get () {
        if (!this.employee.Birth) return null
        return this.employee.Birth ? formatLocalDate(new Date(this.employee.Birth)) : ''
      },
      set (val) {
        this.employee.Birth = val
      }
    },
    imgSrc () {
      if (this.employee && this.employee.Photo && this.employee.Photo.data) {
        const bytes = new Uint8Array(this.employee.Photo.data)

        let mimeType = 'image/jpeg'
        if (bytes.length > 4) {
          if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47) {
            mimeType = 'image/png'
          } else if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) {
            mimeType = 'image/gif'
          }
        }

        let binary = ''
        const len = bytes.byteLength
        for (let i = 0; i < len; i++) {
          binary += String.fromCharCode(bytes[i])
        }

        return `data:${mimeType};base64,` + window.btoa(binary)
      }
      return require('@/assets/profile-placeholder-250x250.png')
    },
    user () {
      return this.$store.getters.getUser
    },
    employeeAM () {
      let empl = this.$store.getters.getAllEmployee
      empl = empl.filter(item => item.Employee_ID === this.user.employee_id)[0]
      if (empl.Status === 'Account Manager (AM)' && empl.Department === this.employee.Department) {
        return true
      }
      return false
    },
    fullName () {
      return `${this.employee.Name || 'Firstname'} ${this.employee.Last_Name || 'Lastname'}`
    },
    canEdit () {
      if (this.notFromEmployeeModule) {
        return false
      } else {
        return this.currentEmployeeRoles.hr.val || this.currentEmployeeRoles.financier.val
      }
    },
    canDelete () {
      return this.canEdit && !this.employee.Is_Deleted && this.employee.Employee_ID
    },
    confirmOrDeniedDialogModes () {
      return this.$store.getters.getConfirmOrDeniedDialogModes
    },
    emplInfoWasChanged () {
      let res = false
      for (const key in this.employee) {
        if (['Empl_Status_History', 'Employee_Rate'].indexOf(key) > -1) continue
        if (this.employee[key] === this.employeeCopy[key]) continue
        res = true
        break
      }
      return res
    },
    canSave () {
      const empl = this.employee
      return !empl.Login || !empl.Name || !empl.Last_Name || !empl.Birth || (!this.employee.Employee_ID && !empl.End_Of_Trial_Period_Date)
    },
    canReviewPerformance () {
      let res = false
      if (this.user.employee_id === this.employee.Supervisor_ID || this.user.employee_id === this.employee.Employee_ID) {
        res = true
      } else if (this.empl_meetings_feedbacks) {
        for (let i = 0; i < this.empl_meetings_feedbacks.length; i++) {
          if (this.empl_meetings_feedbacks[i].Employee_ID === this.user.employee_id) {
            res = true
            break
          }
        }
      } else {
        res = false
      }
      for (let i = 0; i < this.rolesOfCurrentUser.length; i++) {
        if (['HR', 'financier'].indexOf(this.rolesOfCurrentUser[i].RoleName) > -1) {
          res = true
          break
        }
      }
      return res
    },
    canViewPdp () {
      return canViewPrm({
        viewerEmployeeId: this.user.employee_id,
        targetEmployee: this.employee,
        viewerEmployee: this.currentUser
      })
    },
    canViewPrmSummary () {
      return canViewSummary({
        viewerEmployeeId: this.user.employee_id,
        targetEmployee: this.employee,
        viewerEmployee: this.currentUser
      })
    },
    HR_Financier_Manager () {
      let res = false
      for (let i = 0; i < this.rolesOfCurrentUser.length; i++) {
        if (['HR', 'financier', 'manager'].indexOf(this.rolesOfCurrentUser[i].RoleName) > -1) {
          res = true
          break
        }
      }
      return res
    },
    HRorFinancier () {
      let res = false
      for (let i = 0; i < this.rolesOfCurrentUser.length; i++) {
        if (['HR', 'financier'].indexOf(this.rolesOfCurrentUser[i].RoleName) > -1) {
          res = true
          break
        }
      }
      return res
    },
    canAccessMilitaryInfo () {
      if (this.HRorFinancier) return true
      const position = this.$store.getters.getCurrentUserPosition
      return position === 'Personnel accounting manager (military)'
    },
    isFinancier () {
      const requiredRole = 'financier'
      return this.rolesOfCurrentUser.some(userRole =>
        userRole.RoleName.toLowerCase() === requiredRole
      )
    },
    currentEmployee () {
      let res = false
      if (this.user.employee_id === this.employee.Employee_ID) {
        res = true
      }
      return res
    },
    employeeSupervisor () {
      let res = false
      if (this.user.employee_id === this.employee.Supervisor_ID) {
        res = true
      }
      return res
    },
    HR_Financier_AssetsManager () {
      let res = false
      for (let i = 0; i < this.rolesOfCurrentUser.length; i++) {
        if (['HR', 'financier', 'assets_manager'].indexOf(this.rolesOfCurrentUser[i].RoleName) > -1) {
          res = true
          break
        }
      }
      return res
    },
    devComExperience () {
      if (!this.employee.WorkDuration) return null

      const totalDays = this.employee.WorkDuration * 365
      const years = Math.floor(totalDays / 365)
      const months = Math.floor((totalDays % 365) / 30)

      const yearLabel = years === 1 ? 'year' : 'years'
      const monthLabel = months === 1 ? 'month' : 'months'

      if (years && months) {
        return `${years} ${yearLabel} ${months} ${monthLabel}`
      } else if (years) {
        return `${years} ${yearLabel}`
      } else if (months) {
        return `${months} ${monthLabel}`
      }

      return 'No experience'
    },
    workingHours () {
      const res = this.employee.Working_Hours
      const index = res.lastIndexOf(' ', 19)
      const j = res.indexOf(' ', index + 1)
      if (res.length > 25) {
        return res.substring(0, j + 1) + '...'
      } else {
        return res
      }
    },
    emergencyContact () {
      const res = this.Emergency_Contact.Contact_FullName + ', ' + this.Emergency_Contact.Contact_Info
      if (res.length > 25) {
        return res.substring(0, 20) + ' ...'
      } else {
        return res
      }
    },
    currentUser () {
      return this.$store.getters.getAllEmployee.find(el => el.Employee_ID === this.user.employee_id)
    },
    hasPermissionPayroll () {
      return this.showRate()
    }
  },
  async mounted () {
    if (this.employee) {
      Object.assign(this.employeeCopy, this.employee)
      await this.getRolesOfCurrentUser()
      if (this.HRorFinancier) {
        await this.getEmployeeEstimatedInvoiceHistory()
      }
      if (this.employee.Employee_ID) {
        this.getProfilePhoto()
        this.getEmployeeVacationsAndSickLeaves()
        this.getUserPosition()
        this.getEmployeeWorkload()
      }
    }
  },
  methods: {
    formatHours (value) {
      if (value === null || value === undefined || isNaN(value)) return value
      return Number(Math.round(value + 'e2') + 'e-2')
    },
    async getEmployeeEstimatedInvoiceHistory () {
      if (this.employee.Employee_ID) {
        await this.axios.get(`/api/get_employee_estimate_invoice/${this.employee.Employee_ID}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(res => {
          this.historyOfEmployeeEstimateInvoice = res.data
          if (this.historyOfEmployeeEstimateInvoice.length > 0) {
            this.currentEmployeeEstimateInvoice.value = this.historyOfEmployeeEstimateInvoice[this.historyOfEmployeeEstimateInvoice.length - 1].Estimated_Invoice
            this.currentEmployeeEstimateInvoice.from = this.historyOfEmployeeEstimateInvoice[this.historyOfEmployeeEstimateInvoice.length - 1].From_Date
            this.newInvoiceEstimateValue = this.historyOfEmployeeEstimateInvoice[this.historyOfEmployeeEstimateInvoice.length - 1].Estimated_Invoice
          }
        }).catch(error => {
          const errorMsg = getApiErrorMessage(error)
          this.$store.dispatch('setError', { errorMsg })
        })
      }
    },
    showRate () {
      if (this.isFinancier) {
        return true
      }
      if (this.currentEmployee) {
        return true
      }
      if (this.currentUser.Status === 'HR Manager') {
        if (this.employee.Status === 'HR Manager') {
          return false
        }
        if (this.employee.Department === 'HR Department') {
          return false
        }
        return true
      }
      return false
    },
    onEmployeeEstimateInvoiceSave () {
      this.axios.post('/api/save_employee_estimate_invoice', {
        Employee_ID: this.employee.Employee_ID,
        Estimated_Invoice: this.newInvoiceEstimateValue,
        From_Date: formatLocalDate()
      }, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.getEmployeeEstimatedInvoiceHistory()
        this.$store.dispatch('setSuccess', { successMsg: 'Estimate successfully saved' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    getEmployeeWorkload () {
      if (this.employee.Employee_ID) {
        this.axios.get(`/api/get_employee_workload/${this.employee.Employee_ID}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(res => {
          if (res.data.length > 0) {
            this.employeeWorkload = {
              ...res.data[0]
            }
          } else {
            this.employeeWorkload = {
              Employee_Workload_ID: null,
              Employee_ID: this.employee.Employee_ID,
              Workload: null
            }
          }
        }).catch(error => {
          const msg = error.response ? (error.message + ' - ' + JSON.stringify(error.response.data)) : error.message
          this.$store.dispatch('setError', { errorMsg: msg })
        })
      }
    },
    async updateEmployeeWorkload () {
      if (!this.user || !this.user.token) {
        this.$store.dispatch('setError', { errorMsg: 'User not authenticated' })
        return false
      }
      try {
        const response = await this.axios.post('/api/update_employee_workload', {
          Employee_ID: this.employee.Employee_ID,
          Employee_Workload_ID: this.employeeWorkload.Employee_Workload_ID,
          Workload: this.employeeWorkload.Workload
        }, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        if (response.data && response.data.length > 0) {
          this.employeeWorkload = {
            ...response.data[0]
          }
        }
        this.$store.dispatch('setSuccess', { successMsg: 'Work load successfully updated' })
        return true
      } catch (error) {
        let errorMessage = 'Failed to update work load: '
        if (error.response) {
          errorMessage += error.response.data?.message || `Server error: ${error.response.status}`
        } else if (error.request) {
          errorMessage += 'No response from server'
        } else {
          errorMessage += error.message
        }
        this.$store.dispatch('setError', { errorMsg: errorMessage })
        if (error.response && error.response.status === 401) {
          this.$router.push('/login')
        }
        return false
      }
    },
    copyToClipboard (content, target) {
      navigator.clipboard.writeText(content).then(() => {
        let message = `${target} was copied`

        if (target === 'Google Chat') {
          message = 'Email copied! Now paste it into the Google Chat search bar.'
        }

        this.$store.dispatch('setSuccess', { successMsg: message })
      }, () => {
        this.$store.dispatch('setError', { errorMsg: `${target} wasn't copied` })
      })
    },
    getEmployeeCV () {
      if (this.employee.Employee_ID) {
        this.axios.get(`/api/get_employee_cv/${this.employee.Employee_ID}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(res => {
          this.EmployeeCV = {
            ...res.data[0]
          }
        }).catch(error => {
          const errorMsg = getApiErrorMessage(error)
          this.$store.dispatch('setError', { errorMsg })
        })
      }
    },
    getEmployeeCareerMatrix () {
      if (this.employee.Employee_ID) {
        this.axios.get(`/api/get_employee_career_matrix/${this.employee.Employee_ID}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(res => {
          this.Career_Matrix = {
            ...res.data[0]
          }
        }).catch(error => {
          const errorMsg = getApiErrorMessage(error)
          this.$store.dispatch('setError', { errorMsg })
        })
      }
    },
    getEmployeePDPLink () {
      if (this.employee.Employee_ID) {
        this.axios.get(`/api/get_employee_pdp_link/${this.employee.Employee_ID}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(res => {
          this.employeePDP = {
            ...res.data[0]
          }
        }).catch(error => {
          const errorMsg = getApiErrorMessage(error)
          this.$store.dispatch('setError', { errorMsg })
        })
      }
    },
    getSelfReviewSurvey () {
      if (this.employee.Employee_ID) {
        this.axios.get(`/api/get_employee_self_review_survey/${this.employee.Employee_ID}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(res => {
          if (res.data && res.data.length > 0) {
            this.Self_Review_Survey = {
              ...res.data[0]
            }
          } else {
            this.Self_Review_Survey = {
              Performance_Self_Review_ID: null,
              Employee_ID: this.employee.Employee_ID,
              Survey_Form_Link: ''
            }
          }
        }).catch(error => {
          const errorMsg = getApiErrorMessage(error)
          this.$store.dispatch('setError', { errorMsg })
        })
      }
    },
    updateSelfReviewSurvey () {
      this.Self_Review_Survey.Employee_ID = this.employee.Employee_ID
      this.axios.post('/api/update_employee_self_review_survey', this.Self_Review_Survey, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        if (res.data && res.data.length > 0) {
          this.Self_Review_Survey = {
            ...res.data[0]
          }
        }
        this.$store.dispatch('setSuccess', { successMsg: 'Survey Form link successfully updated' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    deleteSelfReviewSurvey () {
      this.axios.delete(`/api/delete_employee_self_review_survey/${this.employee.Employee_ID}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(() => {
        this.Self_Review_Survey = {
          Performance_Self_Review_ID: null,
          Employee_ID: this.employee.Employee_ID,
          Survey_Form_Link: ''
        }
        this.$store.dispatch('setSuccess', { successMsg: 'Survey Form link successfully deleted' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    getSelfReviewResponses () {
      if (this.employee.Employee_ID) {
        this.axios.get(`/api/get_employee_self_review_responses/${this.employee.Employee_ID}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(res => {
          if (res.data && res.data.length > 0) {
            this.Self_Review_Responses = {
              ...res.data[0]
            }
          } else {
            this.Self_Review_Responses = {
              Performance_Self_Review_ID: null,
              Employee_ID: this.employee.Employee_ID,
              Responses_Link: ''
            }
          }
        }).catch(error => {
          const errorMsg = getApiErrorMessage(error)
          this.$store.dispatch('setError', { errorMsg })
        })
      }
    },
    updateSelfReviewResponses () {
      this.Self_Review_Responses.Employee_ID = this.employee.Employee_ID
      this.axios.post('/api/update_employee_self_review_responses', this.Self_Review_Responses, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        if (res.data && res.data.length > 0) {
          this.Self_Review_Responses = {
            ...res.data[0]
          }
        }
        this.$store.dispatch('setSuccess', { successMsg: 'Responses link successfully updated' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    deleteSelfReviewResponses () {
      this.axios.delete(`/api/delete_employee_self_review_responses/${this.employee.Employee_ID}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(() => {
        this.Self_Review_Responses = {
          Performance_Self_Review_ID: null,
          Employee_ID: this.employee.Employee_ID,
          Responses_Link: ''
        }
        this.$store.dispatch('setSuccess', { successMsg: 'Responses link successfully deleted' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    getEmployeeEmergencyContact () {
      if (this.employee.Employee_ID) {
        this.axios.get(`/api/employee/emergency_contact/${this.employee.Employee_ID}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(res => {
          this.Emergency_Contact = {
            ...res.data[0]
          }
        }).catch(error => {
          const errorMsg = getApiErrorMessage(error)
          this.$store.dispatch('setError', { errorMsg })
        })
      }
    },
    updateEmployeeCV () {
      if (!this.EmployeeCV.Employee_ID) {
        this.EmployeeCV.Employee_ID = this.employee.Employee_ID
      }
      this.axios.post('/api/update_employee_cv', this.EmployeeCV, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.$store.dispatch('setSuccess', { successMsg: 'CV successfully updated' })
        this.EmployeeCV = {
          ...res.data[0]
        }
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    deleteEmployeeCV () {
      this.axios.delete(`/api/delete_employee_cv/${this.EmployeeCV.CV_ID}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.$store.dispatch('setSuccess', { successMsg: 'CV successfully deleted' })
        this.EmployeeCV.CV_ID = null
        this.EmployeeCV.CV_Link = ''
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    updateCareerMatrix () {
      if (!this.Career_Matrix.Employee_ID) {
        this.Career_Matrix.Employee_ID = this.employee.Employee_ID
      }
      this.axios.post('/api/update_employee_career_matrix', this.Career_Matrix, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.$store.dispatch('setSuccess', { successMsg: 'Career matrix successfully updated' })
        this.Career_Matrix = {
          ...res.data[0]
        }
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    deleteCareerMatrix () {
      this.axios.delete(`/api/delete_employee_career_matrix/${this.Career_Matrix.Career_Matrix_ID}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.$store.dispatch('setSuccess', { successMsg: 'Career matrix successfully deleted' })
        this.Career_Matrix.Career_Matrix_ID = null
        this.Career_Matrix.Career_Matrix_Link = ''
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    updateEmployeePDPLink () {
      if (!this.employeePDP.Employee_ID) {
        this.employeePDP.Employee_ID = this.employee.Employee_ID
      }
      this.axios.post('/api/update_employee_pdp_link', this.employeePDP, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.employeePDP = {
          ...res.data[0]
        }
        this.$store.dispatch('setSuccess', { successMsg: 'PDP link successfully updated' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    deleteEmployeePDPLink () {
      this.axios.delete(`/api/delete_employee_pdp_link/${this.employeePDP.PDP_Link_ID}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.employeePDP.PDP_Link_ID = null
        this.employeePDP.PDP_Link = ''
        this.$store.dispatch('setSuccess', { successMsg: 'PDP link successfully deleted' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    updateEmergencyContact () {
      if (!this.Emergency_Contact.Employee_ID) {
        this.Emergency_Contact.Employee_ID = this.employee.Employee_ID
      }
      this.axios.post('/api/employee/emergency_contact', this.Emergency_Contact, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.$store.dispatch('setSuccess', { successMsg: 'Emergency contact successfully updated' })
        this.Emergency_Contact = {
          ...res.data[0]
        }
        this.emergencyContactDialog = false
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    changeEmployeePassword () {
      this.disableChangePasswordSave = true
      const reqObj = {
        old: this.changePassword.old,
        new: this.changePassword.new,
        loginname: this.employee.Login
      }
      this.axios.post('/api/employee_password', reqObj, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(result => {
        this.$store.dispatch('setSuccess', { successMsg: 'Password successfully changed' })
        this.disableChangePasswordSave = false
        this.changePassword.showDialog = false
        this.changePassword.old = null
        this.changePassword.oldType = 'password'
        this.changePassword.new = null
        this.changePassword.newType = 'password'
        this.changePassword.confirmNew = null
        this.changePassword.confirmNewType = 'password'
      }).catch(error => {
        this.disableChangePasswordSave = false
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    onPhotoUploaderChange (file) {
      const reader = new FileReader()
      reader.onload = (event) => {
        const img = new Image()
        img.onload = () => {
          if (img.width > 1024 || img.height > 1024) {
            this.showDeniedDialog = true
            this.fileInputValue = null
          } else {
            this.employee.Photo = {}
            this.employee.Photo.data = new Uint8Array(this.dataURLToArrayBuffer(event.target.result))
            this.fileInputValue = null
          }
        }
        img.src = event.target.result
      }
      reader.readAsDataURL(file)
      this.employee.isNewPhoto = true
    },
    dataURLToArrayBuffer (dataURL) {
      const base64 = dataURL.split(',')[1]
      const binary = atob(base64)
      const len = binary.length
      const bytes = new Uint8Array(len)
      for (let i = 0; i < len; i++) {
        bytes[i] = binary.charCodeAt(i)
      }
      return bytes.buffer
    },
    goTo () {
      this.$vuetify.goTo(
        '.data-table tbody tr',
        {
          offset: 10,
          container: '.v-data-table__wrapper'
        }
      )
    },
    openRemoveDialog (key, value) {
      this.showRemove = true
      this.remove.data[key] = value
    },
    closeRemoveDialog () {
      for (const key in this.remove.data) {
        this.remove.data[key] = null
      }
    },
    openMessangerDialog (editData) {
      if (editData) {
        this.messanger.title = 'Edit messenger'
        this.messanger.data.id = editData.IM_ID
        this.messanger.data.title = editData.IM_NAME
        this.messanger.data.account = editData.IM_ACCOUNT
        this.messanger.showDialog = true
      } else {
        this.messanger.title = 'New messanger'
        this.messanger.data.id = null
        this.messanger.data.title = ''
        this.messanger.data.account = ''
        this.messanger.showDialog = true
      }
    },
    saveMessangers () {
      this.disableMessangerSave = true
      if (this.messanger.data.id) {
        this.updateMessanger()
      } else {
        this.addNewMessanger()
      }
    },
    updateMessanger () {
      const body = {
        Employee_ID: this.employee.Employee_ID,
        IM_ID: this.messanger.data.id,
        IM_NAME: this.messanger.data.title,
        IM_ACCOUNT: this.messanger.data.account
      }
      this.axios.put(`/api/employee_im/${body.IM_ID}`, body, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.disableMessangerSave = false
        this.messanger.showDialog = false
        for (let i = 0; i < this.employee.Employee_IM.length; i++) {
          if (this.employee.Employee_IM[i].IM_ID === body.IM_ID) {
            this.$set(this.employee.Employee_IM, i, res.data[0])
            break
          }
        }
        this.$store.dispatch('updateEmployee', { employee: this.employee })
        this.editMode = false
        this.showMessangersDialog = false
        this.$store.dispatch('setSuccess', { successMsg: 'Messanger successfully updated' })
      }).catch(error => {
        this.disableMessangerSave = false
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    addNewMessanger () {
      const body = {
        Employee_ID: this.employee.Employee_ID,
        IM_NAME: this.messanger.data.title,
        IM_ACCOUNT: this.messanger.data.account
      }
      this.axios.post('/api/employee_im', body, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.disableMessangerSave = false
        this.messanger.showDialog = false
        this.editMode = false
        this.showMessangersDialog = false
        this.employee.Employee_IM.push(res.data[0])
        this.$store.dispatch('updateEmployee', { employee: this.employee })
        this.$store.dispatch('setSuccess', { successMsg: 'Messanger successfully added' })
      }).catch(error => {
        this.disableMessangerSave = false
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    deleteMessanger (id) {
      this.axios.delete(`/api/employee_im/${id}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        for (let i = 0; i < this.employee.Employee_IM.length; i++) {
          if (this.employee.Employee_IM[i].IM_ID === id) {
            this.employee.Employee_IM.splice(i, 1)
            break
          }
        }
        this.editMode = false
        this.showMessangersDialog = false
        this.showRemove = false
        this.$store.dispatch('updateEmployee', { employee: this.employee })
        this.$store.dispatch('setSuccess', { successMsg: 'Messanger successfully removed' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    submitRemove () {
      for (const key in this.remove.data) {
        if (this.remove.data[key]) {
          switch (key) {
            case 'messangerId':
              this.deleteMessanger(this.remove.data[key])
              break
          }
          break
        }
      }
    },
    toggleOnCancel () {
      this.showCancelChangesDialog = true
    },
    onCancel () {
      this.editMode = !this.editMode
      if (this.emplInfoWasChanged) Object.assign(this.employee, this.employeeCopy)
      this.getProfilePhoto()
      this.showCancelChangesDialog = false
    },
    async toggleEdit () {
      if (this.editMode) {
        try {
          if (this.employee.Employee_ID && this.employeeWorkload.Workload !== null && this.employeeWorkload.Workload !== undefined) {
            const workloadSaved = await this.updateEmployeeWorkload()
            if (!workloadSaved) {
              console.log('Workload save failed, stopping further operations')
              return
            }
          }
          if (this.employee.Photo && this.employee.Photo.data) {
            this.employee.Photo.base64String = this.imgSrc
          }
          if (this.employee.Previous_Experience >= 0) {
            this.employee.Previous_Experience = parseFloat(+this.employee.Previous_Experience).toFixed(2)
          }
          if (this.employee.Employee_ID) {
            if (this.employee.Dep_ID !== this.employeeCopy.Dep_ID) {
              this.employee.updateDepartment = true
            }
            this.$emit('updateEmployee', this.employee)
          } else {
            this.$emit('createNewEmployee', { ...this.employee, Workload: this.employeeWorkload.Workload ?? null })
          }
          this.editMode = !this.editMode
        } catch (error) {
          console.error('Error in toggleEdit:', error)
          this.$store.dispatch('setError', {
            errorMsg: 'Failed to save changes: ' + (error.message || 'Unknown error')
          })
        }
      } else {
        this.editMode = !this.editMode
      }
    },
    toggleMeetingsEdit () {
      this.meetingEditMode = !this.meetingEditMode
    },
    deleteEmployee () {
      if (this.employee.Photo && this.employee.Photo.data) {
        this.employee.Photo.base64String = this.imgSrc
      }
      this.$emit('updateEmployee', this.employee)
      this.showDeleteEmployeeDialog = false
      this.editMode = false
    },
    restoreEmployee () {
      this.employee.Is_Deleted = false
      this.employee.Left_Date = null
      this.employee.Deleted_Notes = null
      if (this.employee.Photo && this.employee.Photo.data) {
        this.employee.Photo.base64String = this.imgSrc
      }
      this.$emit('updateEmployee', this.employee)
    },
    getEmployeeVacationsAndSickLeaves () {
      this.axios.get(`/api/employee-vacations-and-sick-leaves/${this.employee.Employee_ID}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        const data = response.data
        if (data.SickLeaves) {
          this.absenceItems.push(
            {
              AbsenceType: 'Sick Leaves',
              Used: this.formatHours(data.SickLeaves.Reported),
              Left: this.formatHours(data.SickLeaves.Left)
            })
        }
        if (data.Vacations) {
          this.absenceItems.push(
            {
              AbsenceType: 'Vacations',
              Used: this.formatHours(data.Vacations.Reported),
              Left: this.formatHours(data.Vacations.Left)
            })
        }
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    async getRolesOfCurrentUser () {
      await this.axios.get('/api/employee_role/' + this.user.login, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(result => {
        this.rolesOfCurrentUser = result.data
        let showCareerMatrixAndCV = this.employee.Employee_ID === this.user.employee_id
        if (!showCareerMatrixAndCV) {
          for (let i = 0; i < this.rolesOfCurrentUser.length; i++) {
            if (['HR', 'financier', 'manager'].indexOf(this.rolesOfCurrentUser[i].RoleName) > -1) {
              showCareerMatrixAndCV = true
              break
            }
          }
        }
        if (showCareerMatrixAndCV) {
          this.getEmployeeEmergencyContact()
          this.getEmployeeCV()
          this.getEmployeeCareerMatrix()
          this.getEmployeePDPLink()
          this.getSelfReviewSurvey()
          this.getSelfReviewResponses()
        }
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    getProfilePhoto () {
      if (this.employee.Employee_ID) {
        this.employee.isNewPhoto = false
        this.photoIsLoading = true
        this.axios.get(`/api/employee-photo/${this.employee.Employee_ID}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(result => {
          this.employee.Photo = result.data[0].Photo
          this.photoIsLoading = false
        }).catch(error => {
          const errorMsg = getApiErrorMessage(error)
          this.$store.dispatch('setError', { errorMsg })
        })
      }
    },
    getUserPosition () {
      this.axios.get(`/api/get_user_position/${this.user.employee_id}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        this.$store.dispatch('setCurrentUserPosition', { employeePosition: response.data })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    goToReportsNewPage () {
      this.$router.push({
        name: 'ManagerReports',
        params: {
          Employee_ID: this.employee.Employee_ID
        }
      })
    },
    goToEmployeeSkills () {
      this.editedEmployeeForSkills.employeeId = this.employee.Employee_ID
      this.editedEmployeeForSkills.employeeFullName = this.employee.Full_Name
      this.showCategorySkillsEditor = true
    }
  },
  filters: {
    dateFilter (val) {
      return val ? formatLocalDate(new Date(val)) : ''
    }
  }
}
</script>

<style lang="scss">
.c-employee-details-u .v-dialog {
  max-height: 90vh !important;
  overflow-y: auto;
}

.c-employee-details-u .v-card {
  &.edit-mode {
    min-height: 750px;
    max-height: 85vh;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
  }
}

.c-employee-details-u .v-card__text {
  flex-grow: 1;
  overflow-y: auto;
  padding-bottom: 10px;
}

.c-employee-details-u .v-card__actions {
  flex-shrink: 0;
  padding: 16px;
  min-height: 60px;
  background: white;
  z-index: 2;
}

.c-employee-details-u .v-tabs-items {
  max-height: 60vh;
  overflow-y: auto;
}

.inputAmount input[type='number'] {
    appearance: textfield;
    -webkit-appearance: textfield;
    -moz-appearance: textfield;
}
.inputAmount input::-webkit-outer-spin-button,
.inputAmount input::-webkit-inner-spin-button {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
}
.photo-uploader {
  .v-input__icon--prepend {
    .mdi-pencil-circle {
      color: #1976d2;
    }
  }
}
.close-dialog-btn {
  position: absolute;
  top: 5px;
  right: 5px;
}
.margin-left-80 {
  margin-left: 80px;
}
.loading-photo {
  position: absolute;
  top: 43%;
  left: 30%;
}
.max-width-300 {
  max-width: 300px;
}
.border-thin {
  border: thin solid rgba(0, 0, 0, 0.12)
}
.max-width-400 {
  max-width: 400px;
}
.employee-cellular-container,
.employee-email-container,
.employee-google-chat-container {
  position: relative;
}
.copy-btn {
  position: absolute;
  top: 50%;
  left: 0;
  transform: translate(-100%, -50%);
  z-index: 2;
  display: none;
  transition: opacity .3s ease-in-out;
}
.show-copy-btn {
  &:hover {
    .copy-btn {
      display: block;
    }
  }
}
.workload-input {
  max-width: 120px;
}
.workload-container {
  min-height: 28px;
  display: flex;
  align-items: center;
}
.self-review-menu-card {
  min-width: 160px;
  border-radius: 4px;
}
</style>
